import React, { useCallback } from "react";

import SearchIcon from "@mui/icons-material/Search";
import { Box, Chip, InputAdornment, TextField } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { useQuickViewMetaSelector, useQuickViewQueriesSelect } from "store";
import { QuickViewTableColumn } from "store/types";

import { useSmartSheet } from "../context/SmartSheet-context";
import { getMeasureFilterMode, getMeasureFilterValue, MeasureFilterMode, MeasureInput } from "./MeasureInput";

const getFilterKey = (
  column: QuickViewTableColumn,
  dealFilters: Record<string, (string | number | boolean)[]>
): string | null => {
  if (column.key === "deal" && "deal_name" in dealFilters) return "deal_name";
  if (column.key === "round" && "round" in dealFilters) return column.key;
  if (column.title in dealFilters) return column.title;
  return null;
};

export const SmartSheetColumnFilter: React.FC<{ qvColumn: QuickViewTableColumn }> = ({ qvColumn }) => {
  const { measureFilters, handleMeasureFilterChange, handleFilterChange, containerRef } = useSmartSheet();
  const { dealFilters } = useQuickViewMetaSelector();
  const { filters } = useQuickViewQueriesSelect();

  const handleFilterSelect = useCallback(
    (filterKey: string, values: string[]) => {
      const updated = { ...filters };
      if (values.length) {
        updated[filterKey] = values;
      } else {
        delete updated[filterKey];
      }
      handleFilterChange(updated);
    },
    [filters, handleFilterChange]
  );

  const handleMeasureInputChange = useCallback(
    (columnKey: string, value: number | "", mode: MeasureFilterMode) => {
      const updated = { ...measureFilters };
      if (value === "") {
        delete updated[columnKey];
      } else {
        switch (mode) {
          case MeasureFilterMode.GreaterThan:
            updated[columnKey] = { from_value: value };
            break;
          case MeasureFilterMode.LessThan:
            updated[columnKey] = { to_value: value };
            break;
          case MeasureFilterMode.Equal:
            updated[columnKey] = { from_value: value, to_value: value };
            break;
        }
      }
      handleMeasureFilterChange(updated);
    },
    [measureFilters, handleMeasureFilterChange]
  );

  if (qvColumn.type === "measure") {
    return (
      <Box sx={{ width: "100%" }}>
        <MeasureInput
          columnTitle={qvColumn.title}
          columnKey={qvColumn.key}
          value={getMeasureFilterValue(measureFilters[qvColumn.key])}
          mode={getMeasureFilterMode(measureFilters[qvColumn.key])}
          onChange={handleMeasureInputChange}
          container={containerRef.current}
        />
      </Box>
    );
  }

  const filterKey = getFilterKey(qvColumn, dealFilters);
  if (!filterKey) return null;

  const options = (dealFilters[filterKey] || []).filter((v) => v !== "Blank" && v !== "").map(String);
  if (!options.length) return null;

  const currentValues = filterKey && filters[filterKey]?.length ? filters[filterKey].map(String) : [];

  return (
    <SelectAutocomplete<string, true>
      fullWidth
      multiple
      selectAll
      disableCloseOnSelect
      size="small"
      options={options}
      value={currentValues}
      setValue={(newValues) => handleFilterSelect(filterKey, newValues)}
      renderTags={(tagValue) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", flexShrink: 1, minWidth: 0 }}>
          <Chip label={tagValue[0]} size="small" sx={{ fontSize: 11, height: 20, flexShrink: 1, minWidth: 0 }} />
          {tagValue.length > 1 && (
            <Chip
              label={`+${tagValue.length - 1}`}
              size="small"
              sx={{ flexShrink: 0, fontSize: 11, height: 20, backgroundColor: "#e3f0ff", color: "#1565c0" }}
            />
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={currentValues.length === 0 ? qvColumn.title : undefined}
          variant="outlined"
          sx={{
            "& .MuiInputBase-root": { fontSize: "14px", flexWrap: "nowrap", overflow: "hidden" },
            "& .MuiInputBase-input": {
              height: 16,
              padding: "0 4px",
              minWidth: currentValues.length ? "0 !important" : "40px",
              width: currentValues.length ? 0 : undefined
            }
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start" sx={{ mr: 0, flexShrink: 0 }}>
                  <SearchIcon sx={{ fontSize: 14, color: "#0a3142" }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            )
          }}
        />
      )}
      clearOnEscape
      slotProps={{
        popper: { container: containerRef.current ?? document.body }
      }}
    />
  );
};
